import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Loader2, QrCode, Store, Truck, Upload } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { rupiah } from "../lib/format";
import { TrackingTimeline } from "../components/TrackingTimeline";
import { AuthImage } from "../components/AuthImage";

export default function OrderDetail() {
  const { id } = useParams();
  const [o, setO] = useState(null);
  const [uploading, setUploading] = useState(false);
  const reload = () => api.get(`/orders/${id}`).then((r) => setO(r.data));
  const onProof = async (e) => {
    const f = e.target.files[0];
    if (!f) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", f);
    try {
      const { data } = await api.post("/uploads", fd);
      await api.post(`/orders/${id}/payment-proof`, { proof: data.path });
      toast.success("Bukti transfer terkirim — menunggu verifikasi admin.");
      reload();
    } catch (err) { toast.error("Gagal mengunggah bukti"); } finally { setUploading(false); }
  };
  useEffect(() => {
    api.get(`/orders/${id}`).then((r) => {
      setO(r.data);
      const done = (r.data.tracking || []).filter((x) => x.done);
      const cur = done[done.length - 1];
      if (cur) {
        const hasPhoto = cur.photos && cur.photos.length > 0;
        toast.info(`Update pesanan: ${cur.label}`, {
          description: hasPhoto ? "Foto barang sudah tersedia — cek di pelacakan." : `AWB ${r.data.awb}`,
        });
      }
    });
  }, [id]);

  if (!o) return <div className="py-24 flex justify-center text-emerald-600"><Loader2 className="animate-spin" size={32} /></div>;

  return (
    <div className="max-w-4xl mx-auto px-6 py-8">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="font-display text-2xl font-bold text-stone-900">Detail Pesanan</h1>
          <p className="font-mono text-sm text-stone-400">AWB: {o.awb}</p>
        </div>
        <span className="text-xs font-bold px-3 py-1.5 rounded-full bg-emerald-600 text-white">{o.tracking.find((t) => t.done && o.status === t.key)?.label || o.status}</span>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white rounded-2xl border border-stone-200 p-6">
            <h3 className="font-display font-semibold mb-5">Pelacakan Kiriman (China → Indonesia)</h3>
            <TrackingTimeline tracking={o.tracking} resi={{ GUANGZHOU_HUB: o.resi_china, INDO_WAREHOUSE: o.resi_jakarta }} />
          </div>
          <div className="bg-white rounded-2xl border border-stone-200 p-6">
            <h3 className="font-display font-semibold mb-4">Barang Dipesan</h3>
            <div className="space-y-3">
              {o.items.map((it, i) => (
                <div key={i} className="flex gap-3 items-center">
                  <img src={it.image} alt="" className="w-14 h-14 rounded-lg object-cover" />
                  <div className="flex-1"><p className="text-sm font-medium text-stone-800 line-clamp-1">{it.title}</p><p className="text-xs text-stone-400">{it.qty} × {rupiah(it.unit_price_idr)}</p></div>
                  <p className="font-semibold text-stone-700 text-sm">{rupiah(it.line_total_idr)}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="space-y-6">
          {o.fulfillment === "kedai" && (
            <div className="bg-emerald-900 text-white rounded-2xl p-6 text-center" data-testid="pickup-pass">
              <p className="text-xs uppercase tracking-widest text-emerald-300 flex items-center justify-center gap-1"><Store size={14} />Kode Ambil di Kedai</p>
              <p className="font-mono text-3xl font-extrabold mt-2 tracking-widest">{o.pickup_code}</p>
              <img src={`https://api.qrserver.com/v1/create-qr-code/?size=140x140&data=${o.pickup_code}`} alt="QR" className="mx-auto mt-3 rounded-lg bg-white p-1.5" />
              <p className="text-xs text-emerald-200/70 mt-3">Tunjukkan kode/QR ini ke agen kedai saat mengambil barang.</p>
            </div>
          )}
          {o.payment_status !== "paid" ? (
            <div className="bg-white rounded-2xl border border-amber-200 p-6" data-testid="order-payment">
              <h3 className="font-display font-semibold mb-3 flex items-center gap-2"><QrCode size={18} className="text-amber-500" />Pembayaran QRIS</h3>
              <div className="flex flex-col items-center">
                <img src="/qris-kedaiimpor.jpg" alt="QRIS" data-testid="order-qris" className="w-52 rounded-xl border border-stone-200" />
                <div className="mt-3 bg-amber-50 border border-amber-200 rounded-xl p-3 text-center w-full">
                  <p className="text-xs text-stone-500">Bayar PERSIS sejumlah:</p>
                  <p className="text-2xl font-display font-extrabold text-amber-700" data-testid="order-amount-due">{rupiah(o.amount_due_idr || o.total_idr)}</p>
                  {o.payment_unique_code && <p className="text-xs text-stone-500">Total {rupiah(o.total_idr)} + kode unik <b className="text-amber-700">{o.payment_unique_code}</b></p>}
                </div>
              </div>
              {o.payment_status === "review" ? (
                <div className="mt-4 text-center">
                  <p className="text-sm text-amber-700 font-semibold">Bukti terkirim — menunggu verifikasi admin.</p>
                  {o.payment_proof && <AuthImage path={o.payment_proof} className="mx-auto mt-2 w-32 rounded-lg border border-stone-200" />}
                </div>
              ) : (
                <label className="mt-4 flex items-center justify-center gap-2 border border-dashed border-stone-300 rounded-lg px-3 py-2.5 text-sm text-stone-500 cursor-pointer hover:border-emerald-400" data-testid="order-proof-label">
                  {uploading ? <Loader2 className="animate-spin" size={16} /> : <Upload size={16} />}Unggah bukti transfer
                  <input type="file" accept="image/*" onChange={onProof} className="hidden" data-testid="order-proof-input" />
                </label>
              )}
            </div>
          ) : (
            <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-4 text-center text-emerald-700 text-sm font-semibold" data-testid="order-paid">✓ Pembayaran terverifikasi</div>
          )}
          <div className="bg-white rounded-2xl border border-stone-200 p-6">
            <h3 className="font-display font-semibold mb-4">Rincian Biaya</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between text-stone-600"><span>Subtotal barang</span><b>{rupiah(o.subtotal_idr)}</b></div>
              <div className="flex justify-between text-stone-600"><span>Ongkir ({o.total_weight_kg} kg)</span><b>{rupiah(o.shipping_fee_idr)}</b></div>
              {o.handling_fee_idr > 0 && <div className="flex justify-between text-stone-600"><span>Biaya kedai</span><b>{rupiah(o.handling_fee_idr)}</b></div>}
              <div className="flex justify-between text-base pt-2 border-t border-stone-100"><span className="font-semibold">Total</span><b className="text-emerald-700 font-display">{rupiah(o.total_idr)}</b></div>
            </div>
            <div className="mt-4 pt-4 border-t border-stone-100 text-sm text-stone-500 space-y-1">
              <p className="flex items-center gap-1.5">{o.fulfillment === "kedai" ? <Store size={14} /> : <Truck size={14} />} {o.fulfillment === "kedai" ? "Ambil di kedai" : "Kirim ke alamat"}</p>
              <p>Pembayaran: {o.payment_method}</p>
              {o.address && <p>Alamat: {o.address}</p>}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
